import { NavigationRefs } from 'components/infosite/navigation/navigation-refs';

import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { LocalizedComponentProps } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { UitkCard, UitkCardContent, UitkCardSectionSeparatorType, UitkCardTextSegment } from 'uitk-react-cards';

import { AboutThisPropertyCard } from './internals/about-property-card';
import { FindAnswersInput } from 'components/infosite/find-answers/find-answers-input';
import { ImportantInfo } from './internals/important-info';
import { PropertyInfoSection } from 'components/infosite/property-information/property-info-section/index';
import { PropertyPolicies } from './internals/property-policies';

interface PoliciesProps {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
}

export type LocalizedPoliciesProps = LocalizedComponentProps & PoliciesProps;

export const PropertyInformation: React.FunctionComponent<LocalizedPoliciesProps> = inject(
  'analytics',
  'currentHotel'
)(
  observer(({ analytics, currentHotel }) => {
    const { hotel, offers } = currentHotel || {};
    const { propertyInfo } = offers || {};
    const { policies, importantInfo } = propertyInfo || {};
    const { additionalInfo, questionAnswersViewModel } = hotel || {};
    const {
      propertyAlternativeName,
      propertyDescriptionSection,
      propertyManagerSection,
      propertyAmenitiesSection,
      roomAmenitiesSection,
      entirePlaceSection,
      renovationsAndClosuresSection,
      allInclusiveSection,
      awardsAndAffiliationsSection,
      petsPolicy,
      shouldMention,
      nationalRatingSection
    } = additionalInfo || {};

    return (
      <>
        <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} topPadding={true} />
        <AboutThisPropertyCard
          analytics={analytics}
          propertyDesc={propertyDescriptionSection}
          propertyStructure={entirePlaceSection}
          propertyAllIncl={allInclusiveSection}
          awardsAndAffil={awardsAndAffiliationsSection}
          propertyManager={propertyManagerSection}
        />
        <AmenitiesCard propertyAmenities={propertyAmenitiesSection} roomAmenities={roomAmenitiesSection} analytics={analytics} />
        <PropertyPoliciesCard petsPolicy={petsPolicy} policies={policies} renovationsAndClosuresSection={renovationsAndClosuresSection} />
        <ImportantInfoCard
          importantInfo={importantInfo}
          shouldMention={shouldMention}
          propertyAKA={propertyAlternativeName}
          nationalRating={nationalRatingSection}
        />
        <FindAnswersInput questionAnswersViewModel={questionAnswersViewModel} />
      </>
    );
  })
);

export const AmenitiesCard = props => {
  if (!props.propertyAmenities && !props.roomAmenities) {
    return null;
  }

  return (
    <div className="s-b-padding-three m-b-padding-three" ref={NavigationRefs.Amenities}>
      <UitkCard className="l-y-margin-three xl-y-margin-three">
        <UitkCardContent>
          <Viewport>
            <ViewSmall>
              <AmenitiesContent {...props} />
            </ViewSmall>
            <ViewLarge>
              <AmenitiesContent {...props} gridClass="uitk-grid" />
            </ViewLarge>
          </Viewport>
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

const AmenitiesContent = ({ propertyAmenities, roomAmenities, analytics, gridClass = '' }) => (
  <>
    {propertyAmenities && (
      <UitkCardTextSegment separator={roomAmenities && UitkCardSectionSeparatorType.BOTTOM} className={gridClass}>
        <PropertyInfoSection analytics={analytics} propertyInfoSection={propertyAmenities} isAmenity={true} movePropertyTitle={false} />
      </UitkCardTextSegment>
    )}
    {roomAmenities && (
      <UitkCardTextSegment className={gridClass}>
        <PropertyInfoSection analytics={analytics} propertyInfoSection={roomAmenities} isAmenity={true} movePropertyTitle={false} />
      </UitkCardTextSegment>
    )}
  </>
);

export const PropertyPoliciesCard = ({ petsPolicy, policies, renovationsAndClosuresSection }) => {
  if (!policies) {
    return null;
  }

  return (
    <div className="s-b-padding-three m-b-padding-three" ref={NavigationRefs.Policies}>
      <UitkCard className="l-y-margin-three xl-y-margin-three s-y-padding-three m-y-padding-three all-x-padding-three">
        <UitkCardContent>
          <PropertyPolicies petsPolicy={petsPolicy} policies={policies} renovationsAndClosuresSection={renovationsAndClosuresSection} />
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};

export const ImportantInfoCard = ({ importantInfo, shouldMention, propertyAKA, nationalRating }) => {
  if (!importantInfo) {
    return null;
  }

  return (
    <div className="s-b-padding-three m-b-padding-three">
      <UitkCard className="l-y-margin-three xl-y-margin-three s-y-padding-three m-y-padding-three all-x-padding-three">
        <UitkCardContent>
          <ImportantInfo
            importantInfo={importantInfo}
            shouldMention={shouldMention}
            propertyAKA={propertyAKA}
            nationalRating={nationalRating}
          />
        </UitkCardContent>
      </UitkCard>
    </div>
  );
};
